/**
 * use-i18n-translate.ts — 通用多语言翻译（全站 MultiLangFormRows「从英文翻译」走真 DeepL）
 *
 * 对应后端 POST /api/v1/i18n/translate（operator+）：Key 源 = deepl_credentials 全局凭证；
 * 未配置 → 503 SERVICE_UNAVAILABLE（调用方 toast 引导去「系统设置→集成」配置）。
 * translateOne：单条文本（默认源 en）→ 译文；批量请求壳预留 texts[]。
 */

import type { ConfigTranslationTaskResponse, TranslateI18nResponse } from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '~/api/client';

export const i18nApi = {
  translate: (body: { texts: string[]; source_locale?: string; target_locale: string }) =>
    apiRequest<TranslateI18nResponse>({
      method: 'POST',
      path: '/api/v1/i18n/translate',
      body,
    }),
  listConfigTranslations: () =>
    apiRequest<ConfigTranslationTaskResponse[]>({
      method: 'GET',
      path: '/api/v1/i18n/config-translations',
    }),
  retryConfigTranslation: (id: string) =>
    apiRequest<ConfigTranslationTaskResponse>({
      method: 'POST',
      path: `/api/v1/i18n/config-translations/${id}/retry`,
    }),
};

export function useI18nTranslate() {
  const m = useMutation({ mutationFn: i18nApi.translate });
  /** 单条「从 sourceLocale（默认 en）翻译为 targetLocale」→ 返回译文（失败抛 AdminApiError 供调用方 toast）*/
  const translateOne = async (
    text: string,
    targetLocale: string,
    sourceLocale = 'en',
  ): Promise<string> => {
    const res = await m.mutateAsync({
      texts: [text],
      source_locale: sourceLocale,
      target_locale: targetLocale,
    });
    return res.translations[0] ?? '';
  };
  return { translateOne };
}

export function useConfigTranslationTasks() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['config-translation-tasks'],
    queryFn: i18nApi.listConfigTranslations,
  });
  const retry = useMutation({
    mutationFn: i18nApi.retryConfigTranslation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['config-translation-tasks'] });
    },
  });
  return { tasks: query.data ?? [], isLoading: query.isLoading, retry };
}
